import { useState } from "react";
import { registerUser } from "../services/authService";
import { useNavigate, Link } from "react-router-dom";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import AuthLayout from "../layouts/AuthLayout";
import { Eye, EyeOff } from "lucide-react";

export default function SignupPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    userType: 1,
  });

  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const validate = () => {
    let newErrors = {};

    // Name validation
    if (!form.name.trim()) {
      newErrors.name = "Full name is required";
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!form.email) {
      newErrors.email = "Email is required";
    } else if (!emailRegex.test(form.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    // Phone validation (Basic check for 10+ digits)
  // Phone validation (Exactly 10 digits)
const phoneRegex = /^[0-9]{10}$/;

if (!form.phone) {
  newErrors.phone = "Phone number is required";
} else if (!phoneRegex.test(form.phone)) {
  newErrors.phone = "Phone number must be exactly 10 digits";
}
    // Password validation
    if (!form.password) {
      newErrors.password = "Password is required";
    } else if (form.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    setErrors(newErrors);
    // Returns true if there are no keys in the errors object
    return Object.keys(newErrors).length === 0;
  };

  const handleSignup = async () => {
    setServerError("");

    // 1. Run Validation
    const isValid = validate();

    // 2. Stop if validation fails
    if (!isValid) return;

    try {
      setIsLoading(true);
      await registerUser(form);
      navigate("/login");
    } catch (err) {
      setServerError("Signup failed. Email or phone may already be used.");
    } finally {
      setIsLoading(false);
    }
  };

  // Helper to update form and clear specific error when user types
  const handleChange = (field, value) => {
    setForm({ ...form, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: "" });
    }
  };

  return (
    <AuthLayout
      title="Create an account"
      subtitle="Enter your information to get started."
    >
      <div className="space-y-4">
        {/* FULL NAME */}
        <div>
          <label className="text-sm font-medium mb-1.5 block">Full Name</label>
          <Input
            placeholder="John Doe"
            value={form.name}
            className={
              errors.name ? "border-red-500 focus-visible:ring-red-500" : ""
            }
            onChange={(e) => handleChange("name", e.target.value)}
          />
          {errors.name && (
            <p className="text-red-500 text-xs mt-1">{errors.name}</p>
          )}
        </div>

        {/* EMAIL */}
        <div>
          <label className="text-sm font-medium mb-1.5 block">Email</label>
          <Input
            type="email"
            placeholder="john@example.com"
            value={form.email}
            className={
              errors.email ? "border-red-500 focus-visible:ring-red-500" : ""
            }
            onChange={(e) => handleChange("email", e.target.value)}
          />
          {errors.email && (
            <p className="text-red-500 text-xs mt-1">{errors.email}</p>
          )}
        </div>

        {/* PHONE NUMBER */}
        <div>
          <label className="text-sm font-medium mb-1.5 block">
            Phone Number
          </label>
          <Input
            type="tel"
            placeholder="(555) 000-0000"
            value={form.phone}
            className={
              errors.phone ? "border-red-500 focus-visible:ring-red-500" : ""
            }
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, "");
              if (value.length <= 10) {
                handleChange("phone", value);
              }
            }}
          />
          {errors.phone && (
            <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
          )}
        </div>
        {/* PASSWORD */}
        <div>
          <label className="text-sm font-medium mb-1.5 block">Password</label>
          <div className="relative">
            <Input
              type={showPassword ? "text" : "password"}
              placeholder="Create a password"
              value={form.password}
              className={`pr-10 ${
                errors.password
                  ? "border-red-500 focus-visible:ring-red-500"
                  : ""
              }`}
              onChange={(e) => handleChange("password", e.target.value)}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-black"
            >
              {showPassword ? (
                <EyeOff className="w-4 h-4" />
              ) : (
                <Eye className="w-4 h-4" />
              )}
            </button>
          </div>
          {errors.password && (
            <p className="text-red-500 text-xs mt-1">{errors.password}</p>
          )}
        </div>

        {/* SERVER ERROR */}
        {serverError && (
          <p className="text-red-500 text-sm font-medium text-center">
            {serverError}
          </p>
        )}

        {/* SUBMIT */}
        <Button
          className="w-full"
          size="lg"
          onClick={handleSignup}
          disabled={isLoading}
        >
          {isLoading ? "Creating account..." : "Create account"}
        </Button>

        <p className="text-center text-sm text-muted-foreground">
          Already have an account?{" "}
          <Link
            to="/login"
            className="font-medium text-primary hover:underline"
          >
            Log in
          </Link>
        </p>
      </div>
    </AuthLayout>
  );
}
